import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { softDelete } from '../utils/softDelete';
import VinculacionModal from './VinculacionModal';
import ClienteSearch from './ClienteSearch';

const emptyForm = () => ({
  titulo: '', tipo: 'Audiencia', fecha: '', hora: '00:00', hora_fin: '',
  notas: '', estado: 'pendiente', prioridad: 'normal',
  cliente_id: '', cliente_manual: '', expediente_id: '', tramite_id: '',
});

const emptyBloque = () => ({
  titulo: '', dia_semana: 1, hora_inicio: '08:00', hora_fin: '09:00', color: '#0d9488',
});

const COLORES_BLOQUE = ['#0d9488', '#0891b2', '#4f46e5', '#ea580c', '#65a30d', '#db2777', '#475569', '#9ca3af'];

const DIAS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

export default function EventoModal({ open, onClose, editing = null, onSaved, defaultFecha = '' }) {
  const [clientes, setClientes] = useState([]);
  const [expedientes, setExpedientes] = useState([]);
  const [tramites, setTramites] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [toast, setToast] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [vincularOpen, setVincularOpen] = useState(false);
  const [modo, setModo] = useState('evento');
  const [formBloque, setFormBloque] = useState(emptyBloque());

  const editingId = editing?.id || null;
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  const fetchClientes = useCallback(async () => {
    const { data } = await supabase.from('clientes').select('id, nombre, apellido').eq('eliminado', false).order('apellido');
    setClientes(data || []);
  }, []);

  useEffect(() => {
    if (!open) return;
    setModo('evento');
    setFormBloque(emptyBloque());
    (async () => {
      const [, exp, tra] = await Promise.all([
        fetchClientes(),
        supabase.from('expedientes').select('id, numero, caratula').eq('eliminado', false).order('caratula'),
        supabase.from('tramites').select('id, nombre').eq('eliminado', false).order('nombre'),
      ]);
      setExpedientes(exp.data || []);
      setTramites(tra.data || []);
    })();
    if (editing) {
      setForm({
        titulo: editing.titulo || '', tipo: editing.tipo || 'Audiencia',
        fecha: editing.fecha || '', hora: editing.hora || '', hora_fin: editing.hora_fin || '',
        notas: editing.notas || '', estado: editing.estado || 'pendiente',
        prioridad: editing.prioridad || 'normal',
        cliente_id: editing.cliente_id || '', cliente_manual: editing.cliente_manual || '',
        expediente_id: editing.expediente_id || '', tramite_id: editing.tramite_id || '',
      });
    } else {
      setForm({ ...emptyForm(), fecha: defaultFecha || '' });
    }
  }, [open, editing, defaultFecha, fetchClientes]);

  let vinculo = null;
  if (form.expediente_id) {
    const e = expedientes.find(x => x.id === form.expediente_id);
    vinculo = { icon: '📁', tipo: 'Expediente', label: e ? `${e.numero ? e.numero + ' — ' : ''}${e.caratula || ''}` : 'Expediente' };
  } else if (form.tramite_id) {
    const t = tramites.find(x => x.id === form.tramite_id);
    vinculo = { icon: '📋', tipo: 'Trámite', label: t ? t.nombre : 'Trámite' };
  }

  async function saveEvento() {
    if (guardando) return;
    if (!form.titulo.trim()) { showToast('El título es obligatorio'); return; }
    if (!form.fecha) { showToast('La fecha es obligatoria'); return; }
    setGuardando(true);
    const eventoData = {
      titulo: form.titulo, tipo: form.tipo, fecha: form.fecha, hora: form.hora || null, hora_fin: form.hora_fin || null,
      notas: form.notas, estado: form.estado, prioridad: form.prioridad,
      cliente_id: form.cliente_id || null,
      cliente_manual: form.cliente_id ? null : (form.cliente_manual || null),
      expediente_id: form.expediente_id || null,
      tramite_id: form.tramite_id || null,
    };
    let error;
    if (editingId) {
      ({ error } = await supabase.from('agenda').update(eventoData).eq('id', editingId));
    } else {
      const result = await supabase.from('agenda').insert(eventoData).select('id').single();
      error = result.error;
    }
    setGuardando(false);
    if (error) { showToast('Error al guardar'); return; }
    showToast(editingId ? 'Evento actualizado ✓' : 'Evento agendado ✓');
    onSaved?.();
    onClose();
  }

  async function saveBloque() {
    if (guardando) return;
    if (!formBloque.titulo.trim()) { showToast('El nombre es obligatorio'); return; }
    setGuardando(true);
    const { error } = await supabase.from('bloques_recurrentes').insert({
      titulo: formBloque.titulo,
      dia_semana: formBloque.dia_semana,
      hora_inicio: formBloque.hora_inicio,
      hora_fin: formBloque.hora_fin,
      color: formBloque.color,
      activo: true,
    });
    setGuardando(false);
    if (error) { showToast('Error al guardar'); return; }
    showToast('Tarea periódica guardada ✓');
    onSaved?.();
    onClose();
  }

  async function deleteEvento() {
    if (!window.confirm('¿Eliminar este evento?')) return;
    await softDelete('agenda', editingId);
    onSaved?.();
    onClose();
  }

  if (!open) return null;

  return (
    <>
      <div className="modal-overlay" style={{ zIndex: 900 }} onMouseDown={e => e.target === e.currentTarget && onClose()}>
        <div className="modal" style={{ width: 540 }}>
          <div className="modal-header">
            <div className="modal-title">{editingId ? 'Editar evento' : 'Nuevo evento'}</div>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>

          {/* Toggle modo — solo al crear */}
          {!editingId && (
            <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border2)', padding: '0 22px' }}>
              {[{ id: 'evento', label: 'Evento' }, { id: 'bloque', label: 'Tarea periodica' }].map(m => (
                <button key={m.id} onClick={() => setModo(m.id)} style={{
                  padding: '9px 16px', fontSize: 12, fontWeight: modo === m.id ? 600 : 400,
                  color: modo === m.id ? 'var(--blue)' : 'var(--text3)',
                  background: 'none', border: 'none',
                  borderBottom: modo === m.id ? '2px solid var(--blue)' : '2px solid transparent',
                  cursor: 'pointer', marginBottom: -1,
                }}>{m.label}</button>
              ))}
            </div>
          )}

          <div className="modal-body" style={{ padding: '12px 22px' }}>
            {modo === 'evento' ? (
              <div className="form-grid" style={{ gap: 9 }}>
                <div className="form-group form-full">
                  <label className="form-label">Título *</label>
                  <input className="form-input" value={form.titulo} onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))} placeholder="Descripción del evento" autoFocus />
                </div>
                <div className="form-group">
                  <label className="form-label">Tipo</label>
                  <select className="form-select" value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}>
                    <option>Audiencia</option>
                    <option>Consulta / Turno</option>
                    <option>Reunión</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Prioridad</label>
                  <select className="form-select" value={form.prioridad} onChange={e => setForm(f => ({ ...f, prioridad: e.target.value }))}>
                    <option value="normal">Normal</option>
                    <option value="urgente">Urgente</option>
                    <option value="informativo">Informativo</option>
                  </select>
                </div>
                <div className="form-full" style={{ display: 'flex', gap: 9 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">Fecha *</label>
                    <input className="form-input" type="date" value={form.fecha} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">Hora inicio</label>
                    <input className="form-input" type="time" value={form.hora} onChange={e => setForm(f => ({ ...f, hora: e.target.value }))} />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">Hora fin <span style={{ color: 'var(--text4)', fontWeight: 400 }}>(opcional)</span></label>
                    <input className="form-input" type="time" value={form.hora_fin} onChange={e => setForm(f => ({ ...f, hora_fin: e.target.value }))} />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">Estado</label>
                    <select className="form-select" value={form.estado} onChange={e => setForm(f => ({ ...f, estado: e.target.value }))}>
                      <option value="pendiente">Pendiente</option>
                      <option value="realizado">Realizado</option>
                      <option value="cancelado">Cancelado</option>
                    </select>
                  </div>
                </div>

                {!vinculo && (
                  <div className="form-group form-full">
                    <label className="form-label">Cliente</label>
                    <ClienteSearch
                      clientes={clientes}
                      value={form.cliente_id}
                      onChange={id => setForm(f => ({ ...f, cliente_id: id, cliente_manual: '' }))}
                      allowManual
                      manualValue={form.cliente_manual}
                      onManualChange={v => setForm(f => ({ ...f, cliente_manual: v }))}
                      allowCreate
                      onClienteCreado={fetchClientes}
                      placeholder="Buscar cliente por nombre o DNI..."
                    />
                  </div>
                )}

                <div className="form-group form-full">
                  <label className="form-label">Expediente / Trámite</label>
                  {vinculo ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: 'var(--surface2)', border: '0.5px solid var(--border)', borderRadius: 8 }}>
                      <span style={{ fontSize: 16, flexShrink: 0 }}>{vinculo.icon}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 10, color: 'var(--text4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{vinculo.tipo}</div>
                        <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{vinculo.label}</div>
                      </div>
                      <button className="btn btn-ghost btn-sm" onClick={() => setVincularOpen(true)} style={{ flexShrink: 0 }}>Cambiar</button>
                    </div>
                  ) : (
                    <button className="btn btn-ghost" onClick={() => setVincularOpen(true)} style={{ width: '100%', justifyContent: 'center' }}>
                      + Vincular expediente o trámite
                    </button>
                  )}
                </div>

                {!editingId && (
                  <div className="form-group form-full">
                    <label className="form-label">Notas</label>
                    <textarea className="form-textarea" value={form.notas} onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} placeholder="Detalles adicionales..." style={{ resize: 'none', minHeight: 48 }} />
                  </div>
                )}
              </div>
            ) : (
              <div className="form-grid" style={{ gap: 9 }}>
                <div className="form-group form-full">
                  <label className="form-label">Nombre *</label>
                  <input className="form-input" value={formBloque.titulo} onChange={e => setFormBloque(f => ({ ...f, titulo: e.target.value }))} placeholder="Ej: Guardia, Reunion de equipo, Clases..." autoFocus />
                </div>
                <div className="form-group form-full">
                  <label className="form-label">Dia de la semana</label>
                  <select className="form-select" value={formBloque.dia_semana} onChange={e => setFormBloque(f => ({ ...f, dia_semana: Number(e.target.value) }))}>
                    {DIAS.map((d, i) => <option key={i} value={i + 1}>{d}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Desde</label>
                  <input className="form-input" type="time" value={formBloque.hora_inicio} onChange={e => setFormBloque(f => ({ ...f, hora_inicio: e.target.value }))} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Hasta</label>
                  <input className="form-input" type="time" value={formBloque.hora_fin} onChange={e => setFormBloque(f => ({ ...f, hora_fin: e.target.value }))} />
                </div>
                <div className="form-group form-full">
                  <label className="form-label">Color</label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
                    {COLORES_BLOQUE.map(c => (
                      <div key={c} onClick={() => setFormBloque(f => ({ ...f, color: c }))}
                        style={{ width: 28, height: 28, borderRadius: '50%', background: c, cursor: 'pointer', border: formBloque.color === c ? '3px solid var(--text)' : '3px solid transparent', flexShrink: 0 }} />
                    ))}
                  </div>
                </div>
                <div className="form-group form-full">
                  <div style={{ padding: '10px 14px', background: 'var(--surface2)', border: '1px solid var(--border2)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: formBloque.color, flexShrink: 0 }} />
                    <div style={{ fontSize: 12, color: 'var(--text2)' }}>
                      <strong>{formBloque.titulo || 'Sin nombre'}</strong>
                      {' — '}{DIAS[(formBloque.dia_semana || 1) - 1]}
                      {' '}{formBloque.hora_inicio} a {formBloque.hora_fin}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            {editingId && (
              <button className="btn btn-danger btn-sm" onClick={deleteEvento} style={{ marginRight: 'auto' }}>Eliminar</button>
            )}
            <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
            <button className="btn btn-primary" onClick={modo === 'evento' ? saveEvento : saveBloque} disabled={guardando}>
              {guardando ? 'Guardando...' : modo === 'evento' ? 'Guardar evento' : 'Guardar tarea periodica'}
            </button>
          </div>
        </div>
      </div>

      <VinculacionModal
        open={vincularOpen}
        onClose={() => setVincularOpen(false)}
        tabs={['expedientes', 'tramites']}
        value={{ expediente_id: form.expediente_id, tramite_id: form.tramite_id }}
        onSelect={(v) => setForm(f => ({ ...f, ...v }))}
      />

      {toast && <div className="toast" style={{ zIndex: 1200 }}>✓ {toast}</div>}
    </>
  );
}
